import React from 'react';
import FileCard from './FileCard';
import FileCardEnhanced from './FileCardEnhanced';
import { KnowledgeFile } from '../../types';
import { File, FileText, Image, Video, Music, Archive, Code, Table, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

export type SortField = 'name' | 'size' | 'type' | 'updatedAt';
export type SortOrder = 'asc' | 'desc';

interface FileGridProps {
  files: KnowledgeFile[];
  isLoading?: boolean;
  emptyMessage?: string;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectChange?: (id: string, checked: boolean) => void;
  viewMode?: 'grid' | 'list';
  sortField?: SortField;
  sortOrder?: SortOrder;
  onSort?: (field: SortField) => void;
}

// 获取文件类型图标和颜色
const getFileTypeIcon = (file: KnowledgeFile) => {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const type = file.type;

  // PDF
  if (ext === 'pdf') {
    return { icon: FileText, color: 'text-red-500', bg: 'bg-red-50' };
  }
  // Word
  if (['doc', 'docx'].includes(ext)) {
    return { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' };
  }
  // Excel
  if (['xls', 'xlsx', 'csv'].includes(ext)) {
    return { icon: Table, color: 'text-green-600', bg: 'bg-green-50' };
  }
  // PPT
  if (['ppt', 'pptx'].includes(ext)) {
    return { icon: FileText, color: 'text-orange-500', bg: 'bg-orange-50' };
  }
  // 代码文件
  if (['js', 'ts', 'tsx', 'jsx', 'py', 'java', 'cpp', 'c', 'go', 'rs', 'md', 'json', 'xml', 'html', 'css'].includes(ext)) {
    return { icon: Code, color: 'text-purple-600', bg: 'bg-purple-50' };
  }
  // 压缩包
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
    return { icon: Archive, color: 'text-amber-600', bg: 'bg-amber-50' };
  }
  // 按类型
  if (type === 'image') {
    return { icon: Image, color: 'text-pink-500', bg: 'bg-pink-50' };
  }
  if (type === 'video') {
    return { icon: Video, color: 'text-indigo-500', bg: 'bg-indigo-50' };
  }
  if (type === 'audio') {
    return { icon: Music, color: 'text-cyan-500', bg: 'bg-cyan-50' };
  }
  if (type === 'document') {
    return { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' };
  }

  // 默认
  return { icon: File, color: 'text-gray-400', bg: 'bg-gray-100' };
};

const FileGrid: React.FC<FileGridProps> = ({
  files,
  isLoading = false,
  emptyMessage = "No files found",
  selectable = false,
  selectedIds,
  onSelectChange,
  viewMode = 'grid',
  sortField,
  sortOrder,
  onSort,
}) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="w-16 h-16 mb-4 text-gray-300">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  // 格式化文件大小
  const formatFileSize = (bytes: number) => {
    if (!bytes || bytes === 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // 格式化日期 - 使用dayjs与FileList保持一致
  const formatDate = (dateString: string | Date | undefined) => {
    if (!dateString) return '-';
    const d = dayjs(dateString);
    if (!d.isValid()) return '-';
    return d.format('YYYY-MM-DD HH:mm');
  };

  // 排序图标组件
  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (!onSort) return null;
    if (sortField !== field) {
      return <ArrowUpDown size={12} className="ml-1 text-gray-300" />;
    }
    return sortOrder === 'asc'
      ? <ArrowUp size={12} className="ml-1 text-teal-500" />
      : <ArrowDown size={12} className="ml-1 text-teal-500" />;
  };

  // 列表视图
  if (viewMode === 'list') {
    return (
      <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
        {/* 可排序表头 */}
        <div className="hidden sm:grid grid-cols-12 gap-3 sm:gap-4 px-3 sm:px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide bg-gray-50">
          <button
            onClick={() => onSort?.('name')}
            className="col-span-5 flex items-center hover:text-gray-700 transition-colors text-left"
          >
            文件名
            <SortIcon field="name" />
          </button>
          <button
            onClick={() => onSort?.('size')}
            className="col-span-2 flex items-center hover:text-gray-700 transition-colors text-left"
          >
            大小
            <SortIcon field="size" />
          </button>
          <button
            onClick={() => onSort?.('type')}
            className="col-span-2 flex items-center hover:text-gray-700 transition-colors text-left"
          >
            类型
            <SortIcon field="type" />
          </button>
          <button
            onClick={() => onSort?.('updatedAt')}
            className="col-span-3 flex items-center hover:text-gray-700 transition-colors text-left"
          >
            修改时间
            <SortIcon field="updatedAt" />
          </button>
        </div>
        {/* 文件列表 */}
        {files.map((file) => {
          const typeInfo = getFileTypeIcon(file);
          const IconComponent = typeInfo.icon;
          return (
            <div
              key={file.id}
              className="px-3 sm:px-4 py-3 sm:py-4 hover:bg-gray-50 transition-colors cursor-pointer group bg-white"
              onClick={() => navigate(`/uadmin/files/${file.id}`)}
            >
              {/* 移动端布局 */}
              <div className="sm:hidden space-y-2">
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-lg ${typeInfo.bg} flex items-center justify-center mr-3 flex-shrink-0`}>
                    <IconComponent size={18} className={typeInfo.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{file.name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                      <span className="text-gray-300">•</span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${typeInfo.bg} ${typeInfo.color} capitalize`}>
                        {file.type}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-400 pl-[52px]">
                  {formatDate(file.updatedAt)}
                </div>
              </div>

              {/* 桌面端布局 */}
              <div className="hidden sm:grid grid-cols-12 gap-3 sm:gap-4 items-center">
                <div className="col-span-5 flex items-center min-w-0">
                  <div className={`w-8 h-8 rounded-lg ${typeInfo.bg} flex items-center justify-center mr-3 flex-shrink-0`}>
                    <IconComponent size={16} className={typeInfo.color} />
                  </div>
                  <span className="text-sm font-medium text-gray-800 truncate">{file.name}</span>
                </div>
                <div className="col-span-2 text-sm text-gray-500">
                  {formatFileSize(file.size)}
                </div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeInfo.bg} ${typeInfo.color} capitalize`}>
                    {file.type}
                  </span>
                </div>
                <div className="col-span-3 text-sm text-gray-500">
                  {formatDate(file.updatedAt)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // 网格视图（默认）- 使用容器查询实现响应式列数
  // Uses FileCardEnhanced with progressive animation (Requirement 6.5)
  return (
    <div className="file-grid-container">
      <div 
        className="file-grid-shelf grid"
        style={{
          gridTemplateColumns: 'repeat(var(--file-grid-columns), minmax(var(--file-grid-item-min-width), 1fr))',
          gap: 'var(--file-grid-gap)',
        }}
      >
        {files.map((file, index) => (
          <FileCardEnhanced
            key={file.id}
            file={file}
            selectable={selectable}
            selected={selectedIds?.has(file.id)}
            onSelectChange={onSelectChange}
            animate={true}
            index={index}
            totalItems={files.length}
          />
        ))}
      </div>
    </div>
  );
};

export default FileGrid;